import { useRef, useState } from 'react';
import type { TouchEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';
import { css } from '@emotion/css';

import type { Recuerdo } from '#components/mobile/budget/recuerdos';
import { aspectoDeRecuerdo } from '#components/mobile/budget/recuerdosAspecto';
import { Botella } from '#components/mobile/budget/travesia/Botella';
import {
  claseEscena,
  e,
  GRANO,
  SERIF,
} from '#components/mobile/budget/travesia/escenaCss';
import { Icono } from '#components/mobile/ui/Icono';
import { TACTIL } from '#components/mobile/ui/tokens';
import { useLocale } from '#hooks/useLocale';

import { acotarPagina, cuadernoDe, nombreDe, paginaDeRid } from './bitacora';
import type { Cumplida } from './bitacora';
import {
  PaginaNavegando,
  PaginaTotales,
  PaginaTravesia,
  PaginaVacia,
} from './PaginasCuaderno';
import type { EnCurso } from './PaginasCuaderno';

type EscritorioProps = {
  recuerdos: readonly Recuerdo[];
  /** Categorías ocultas: sus travesías en curso no salen en «Navegando». */
  ocultas?: ReadonlySet<string>;
  hoy: string;
  noche: boolean;
  reducido: boolean;
  /** Rid de las botellas llegadas que este dispositivo aún no ha abierto. */
  porAbrir: ReadonlySet<string>;
  /** Lo que llevan las travesías en curso (por rid), si se sabe. */
  enCurso: ReadonlyMap<string, EnCurso>;
  /** Página con la que se abre el cuaderno (rid de una travesía). */
  ridInicial?: string | null;
  onAbrirBotella: (rid: string) => void;
  onIrCategoria: (categoriaId: string) => void;
};

/** Botellas de la repisa: ancho de cada una y hueco que ocupa. */
const ANCHO_MINI = 26;

/**
 * El escritorio del armador (F1 §2): arriba la repisa con las botellas ya
 * abiertas (filas que crecen; tocar una abre su página), delante el
 * cuaderno paginado y, al final, el cajón «Archivadas», plegado.
 */
export function Escritorio({
  recuerdos,
  ocultas,
  hoy,
  noche,
  reducido,
  porAbrir,
  enCurso,
  ridInicial,
  onAbrirBotella,
  onIrCategoria,
}: EscritorioProps) {
  const { t } = useTranslation();
  const cuaderno = cuadernoDe(recuerdos, ocultas);
  const { paginas, cumplidas } = cuaderno;
  const [pagina, setPagina] = useState(() => paginaDeRid(paginas, ridInicial));
  const [cajonAbierto, setCajonAbierto] = useState(false);
  const libro = useRef<HTMLDivElement>(null);
  const toque = useRef<{ x: number; y: number } | null>(null);

  const actual = acotarPagina(pagina, paginas.length);
  const ir = (i: number) => setPagina(acotarPagina(i, paginas.length));

  const elegirBotella = (c: Cumplida) => {
    ir(paginaDeRid(paginas, c.recuerdo.base.rid));
    libro.current?.scrollIntoView?.({
      block: 'start',
      behavior: reducido ? 'auto' : 'smooth',
    });
  };

  const empezarToque = (ev: TouchEvent) => {
    const p = ev.touches[0];
    toque.current = p ? { x: p.clientX, y: p.clientY } : null;
  };
  const acabarToque = (ev: TouchEvent) => {
    const inicio = toque.current;
    const p = ev.changedTouches[0];
    toque.current = null;
    if (!inicio || !p) {
      return;
    }
    const dx = p.clientX - inicio.x;
    const dy = p.clientY - inicio.y;
    if (Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      ir(actual + (dx < 0 ? 1 : -1));
    }
  };

  const p = paginas[actual];

  return (
    <div
      className={[
        claseEscena,
        claseEscritorio,
        noche ? 'noche' : '',
        reducido ? 'quieta' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="escritorio"
    >
      <div
        className="repisa"
        role="list"
        aria-label={t('Logbook shelf')}
        data-testid="repisa"
        data-botellas={cumplidas.length}
      >
        {cumplidas.map(c => (
          <div key={c.recuerdo.base.rid} role="listitem" className="hueco">
            <button
              type="button"
              className="mini"
              aria-label={nombreDe(c.recuerdo)}
              aria-current={
                p?.tipo === 'travesia' &&
                p.cumplida.recuerdo.base.rid === c.recuerdo.base.rid
                  ? 'page'
                  : undefined
              }
              data-testid="repisa-botella"
              onClick={() => elegirBotella(c)}
            >
              <Botella
                aspecto={aspectoDeRecuerdo(c.recuerdo, hoy)}
                abierta={!porAbrir.has(c.recuerdo.base.rid)}
                ancho={ANCHO_MINI}
              />
            </button>
          </div>
        ))}
      </div>

      <div ref={libro} className="libro" data-testid="cuaderno">
        {paginas.length > 1 && (
          <nav className="pasar" aria-label={t('Logbook')}>
            <button
              type="button"
              aria-label={t('Previous logbook page')}
              disabled={actual === 0}
              onClick={() => ir(actual - 1)}
              data-testid="cuaderno-anterior"
            >
              <Icono nombre="cl" size={20} />
            </button>
            <span className="tnum" data-testid="cuaderno-pagina">
              {t('Page {{page}} of {{total}}', {
                page: actual + 1,
                total: paginas.length,
              })}
            </span>
            <button
              type="button"
              aria-label={t('Next logbook page')}
              disabled={actual >= paginas.length - 1}
              onClick={() => ir(actual + 1)}
              data-testid="cuaderno-siguiente"
            >
              <Icono nombre="cr" size={20} />
            </button>
          </nav>
        )}
        <article
          key={actual}
          className="hoja"
          data-testid="cuaderno-hoja"
          data-tipo={p?.tipo}
          onTouchStart={empezarToque}
          onTouchEnd={acabarToque}
        >
          {p?.tipo === 'vacia' && (
            <PaginaVacia hayNavegando={cuaderno.navegando.length > 0} />
          )}
          {p?.tipo === 'totales' && (
            <PaginaTotales totales={cuaderno.totales} />
          )}
          {p?.tipo === 'travesia' && (
            <PaginaTravesia
              cumplida={p.cumplida}
              hoy={hoy}
              porAbrir={porAbrir.has(p.cumplida.recuerdo.base.rid)}
              onAbrir={() => onAbrirBotella(p.cumplida.recuerdo.base.rid)}
            />
          )}
          {p?.tipo === 'navegando' && (
            <PaginaNavegando
              recuerdos={cuaderno.navegando}
              hoy={hoy}
              enCurso={enCurso}
              onIrCategoria={onIrCategoria}
            />
          )}
        </article>
      </div>

      {cuaderno.archivadas.length > 0 && (
        <Cajon
          abierto={cajonAbierto}
          onAlternar={() => setCajonAbierto(a => !a)}
          archivadas={cuaderno.archivadas}
        />
      )}
    </div>
  );
}

function Cajon({
  abierto,
  onAlternar,
  archivadas,
}: {
  abierto: boolean;
  onAlternar: () => void;
  archivadas: readonly Recuerdo[];
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const fecha = (dia: string) => monthUtils.format(dia, 'd MMM yyyy', locale);
  return (
    <section className="cajon" data-testid="cajon">
      <button
        type="button"
        className="tirador"
        aria-expanded={abierto}
        onClick={onAlternar}
        data-testid="cajon-tirador"
      >
        <span>
          <Trans>Archived voyages</Trans>
        </span>
        <Icono nombre={abierto ? 'cu' : 'cd'} size={18} />
      </button>
      {abierto && (
        <ul className="archivo" data-testid="cajon-lista">
          {archivadas.map(r => (
            <li key={r.base.rid}>
              <div className="nombre">{nombreDe(r)}</div>
              <div className="fechas tnum">
                {t('Set sail on {{date}}', { date: fecha(r.base.zarpoEn) })}
                {r.base.cerradaEn &&
                  ` · ${t('Archived on {{date}}', { date: fecha(r.base.cerradaEn) })}`}
              </div>
              {r.base.carta && (
                <p className="carta-archivada">{r.base.carta}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const PASO_BALDA = 86;
const GROSOR_BALDA = 12;

const claseEscritorio = css({
  '--madera': '#3b2f25',
  '--madera-2': '#2a211a',
  '&.noche': { '--madera': '#1a140f', '--madera-2': '#0f0b08' },
  position: 'relative',
  minHeight: '100%',
  boxSizing: 'border-box',
  background:
    'linear-gradient(var(--madera-2), var(--madera) 240px, var(--madera))',
  color: e.papel,
  paddingBottom: 24,
  '& .repisa': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    margin: '18px 0 0',
    padding: '0 14px',
    minHeight: PASO_BALDA,
    boxSizing: 'border-box',
    // Una balda de madera bajo cada fila (las filas crecen con las botellas).
    backgroundImage: `repeating-linear-gradient(to bottom, transparent 0 ${PASO_BALDA - GROSOR_BALDA}px, #6b5440 ${PASO_BALDA - GROSOR_BALDA}px, #4a3a2c ${PASO_BALDA}px)`,
  },
  '& .hueco': {
    height: PASO_BALDA,
    width: 44,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingBottom: GROSOR_BALDA,
    boxSizing: 'border-box',
  },
  '& .mini': {
    minWidth: TACTIL,
    minHeight: TACTIL,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 0,
    border: 0,
    background: 'none',
    cursor: 'pointer',
    borderRadius: 6,
    filter: 'drop-shadow(0 3px 3px rgba(0,0,0,.35))',
  },
  '& .mini[aria-current]': {
    boxShadow: `0 ${GROSOR_BALDA - 2}px 0 -6px ${e.laton2}`,
  },
  '& .libro': {
    margin: '16px 14px 0',
    scrollMarginTop: 8,
  },
  '& .pasar': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    fontFamily: SERIF,
    fontSize: 15,
    color: e.papel,
    '& button': {
      width: TACTIL,
      height: TACTIL,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 0,
      borderRadius: 22,
      background: 'rgba(226,214,189,.08)',
      color: e.papel,
      cursor: 'pointer',
    },
    '& button:disabled': { opacity: 0.3, cursor: 'default' },
  },
  '& .hoja': {
    position: 'relative',
    boxSizing: 'border-box',
    minHeight: 420,
    background: e.papel,
    backgroundImage: GRANO,
    color: e.tinta,
    borderRadius: '4px 4px 3px 3px',
    padding: '22px 22px 24px',
    fontFamily: SERIF,
    boxShadow:
      '0 10px 30px rgba(0,0,0,.3), inset 26px 0 26px -24px rgba(90,60,30,.35)',
    overflowWrap: 'anywhere',
  },
  '& .cajon': {
    margin: '22px 14px 0',
    borderRadius: 6,
    background: 'linear-gradient(#5a4634, #45362a)',
    boxShadow:
      '0 6px 14px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.08)',
  },
  '& .tirador': {
    width: '100%',
    minHeight: TACTIL + 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    border: 0,
    background: 'none',
    color: e.papel,
    fontFamily: SERIF,
    fontSize: 16,
    letterSpacing: '.08em',
    fontVariant: 'small-caps',
    textTransform: 'lowercase',
    cursor: 'pointer',
  },
  '& .archivo': {
    listStyle: 'none',
    margin: 0,
    padding: '0 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  '& .archivo li': {
    background: e.papel2,
    backgroundImage: GRANO,
    color: e.tinta,
    borderRadius: 3,
    padding: '12px 14px',
    fontFamily: SERIF,
    overflowWrap: 'anywhere',
  },
  '& .archivo .nombre': { fontSize: 18 },
  '& .archivo .fechas': { fontSize: 13.5, color: e.tinta2, marginTop: 2 },
  '& .carta-archivada': {
    margin: '8px 0 0',
    fontStyle: 'italic',
    fontSize: 15,
    lineHeight: 1.45,
    whiteSpace: 'pre-line',
  },
});
